import React, { FC, ReactNode } from 'react';
import { Button, Icon, TextField } from '@mui/material';
import InputMask from 'react-input-mask';
import styles from './Keyboard.module.scss';

export interface IKeyboardInputMaskedProps {
  id?: string;
  mask: string | (string | RegExp)[];
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (ref: React.RefObject<HTMLInputElement>, value: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  autoFocus?: boolean;
  size?: 'small' | 'medium';
  className?: string;
  labelStyle?: React.CSSProperties;
  inputMode?: 'numeric' | 'text';
  showSubmitButton?: boolean;
  submitButtonLabel?: ReactNode;
  focusOnSubmit?: boolean;
  setCurrentKey?: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const KeyboardInputMasked: FC<IKeyboardInputMaskedProps> = ({
  id,
  mask,
  value,
  onChange,
  onSubmit,
  inputRef,
  label,
  placeholder,
  error,
  required = false,
  autoFocus,
  size = 'small',
  className,
  labelStyle,
  inputMode = 'numeric',
  showSubmitButton,
  submitButtonLabel,
  focusOnSubmit,
  setCurrentKey,
  disabled,
  onFocus,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setCurrentKey?.(event.key);

    if (event.key === 'Enter' && !event.shiftKey) {
      const input = inputRef.current;
      if (!input) return;

      onSubmit?.(inputRef, input.value);
      input.setSelectionRange(0, input.value.length);

      if (!focusOnSubmit) {
        input.blur();
      }
    }
  };

  const handleClickSend = () => {
    const input = inputRef.current;
    if (!input) return;

    onSubmit?.(inputRef, input.value);
    input.setSelectionRange(0, input.value.length);

    if (!focusOnSubmit) {
      input.blur();
    }
  };

  return (
    <div className={styles.inputContainer}>
      {label && (
        <label
          className={`${styles.label} ${required ? styles.required : ''}`}
          style={labelStyle}
        >
          {label}
        </label>
      )}

      <InputMask
        mask={mask}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        onFocus={onFocus}
      >
        {() => (
          <TextField
            id={id}
            size={size}
            autoFocus={autoFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={className}
            error={!!error}
            helperText={error}
            inputRef={inputRef}
            fullWidth
            inputProps={{
              inputMode,
            }}
            InputProps={{
              endAdornment: showSubmitButton ? (
                <Button variant="contained" onClick={handleClickSend}>
                  {submitButtonLabel ?? <Icon>check</Icon>}
                </Button>
              ) : undefined,
            }}
          />
        )}
      </InputMask>
    </div>
  );
};

export default KeyboardInputMasked;
